import { Component } from '@angular/core';
import { TimeCompositionListEnum } from '@app/shared/constants/global.enum';
import { Router } from '@angular/router';
import { SearchService } from '@app/shared/services/search.service';
import { GlobalErrorService } from '@app/shared/services/global-error.service';
import { Priority } from '@constants/global.enum';
import { TeamCompositionListInfo } from '@app/shared/constants/constants';
import { TeamManagementService } from '../team-management.service';
import { AuthService } from '@app/core/auth.service';
import { ErrorMessages } from '@constants/constants';
import { TeamCompositionList } from '@app/shared/interfaces/team-management.model';
import { dropdownOptions } from '@app/shared/interfaces/vendor.model';
import { Subject, takeUntil, filter } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
@Component({
  selector: 'app-team-composition-list',
  templateUrl: './team-composition-list.component.html',
  styleUrl: './team-composition-list.component.scss',
})
export class TeamCompositionListComponent {
  imageSrc: string = 'assets/img/no-data-found.png';
  isDataLoaded: boolean = false;
  TimeCompositionListLabels = TimeCompositionListEnum;
  routesEnums = TeamCompositionListInfo;
  data: TeamCompositionList[] = [];
  filterValue: string = '';
  priority = Priority;
  locationOptions: dropdownOptions[] = [];
  selectedLocation: string = '';
  resourceTypeOptions: dropdownOptions[] = [];
  selectedResourceType: string = '';
  employeeId: string = '';
  displayData: TeamCompositionList[] = [];
  errorMsg: string = ErrorMessages.NoDataAvailable;
  showTeamDetailsModal: boolean = false;
  selectedProfileId: string | null = null;
  showContractorProfileModal: boolean = false;
  selectedContractorId: string | null = null;

  // Row actions (Exclude/Include in iSkill, Mark Inactive) — both confirmed first
  actionRow: TeamCompositionList | null = null;
  showExclusionDialog: boolean = false;
  showDeactivateDialog: boolean = false;
  lastWorkingDay: string = '';
  actionInProgress: boolean = false;

  // Custom table state (replaces emerald-datatable)
  sortField: keyof TeamCompositionList = 'name';
  sortDir: 1 | -1 = 1;
  page = 1;
  readonly pageSize = 10;
  private destroy$ = new Subject<void>();
  
  constructor(
    private searchService: SearchService,
    private router: Router,
    private errorHandler: GlobalErrorService,
    private teamManagementService: TeamManagementService,
    private authService: AuthService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {

    // Subscribe to dropdown changes
    this.teamManagementService.getSelectedDropdownValue()
      .pipe(
        takeUntil(this.destroy$),
        filter(selection => selection !== null)
      )
      .subscribe((selection) => {
        // Fetch data with the selected employee's ID and filterType from dropdown
        this.loadTimeCompositionListData(selection!.employeeId, selection!.filterType);
      });

    this.loadTimeCompositionListData();
  }

  async loadTimeCompositionListData(selectedEmployeeId?: string, filterType?: string): Promise<void> {
    try {
      // Use provided employeeId or get from auth service
      let employeeIdToUse = selectedEmployeeId;
      
      if (!employeeIdToUse) {
        const currentSelection =
          this.teamManagementService.getCurrentSelectedDropdownValue();

        if (currentSelection) {
          employeeIdToUse = currentSelection.employeeId;
          filterType = currentSelection.filterType;
        }
      }

      if (!employeeIdToUse) {
        const profile = await this.authService.getEmployeeDetails();
        if (profile?.employeeId) {
          this.employeeId = profile?.employeeId;
          employeeIdToUse = this.employeeId;
        }
      }

      if (!employeeIdToUse) {
        this.errorMsg = TeamCompositionListInfo.FailedToLoadTeamCompositionData;
        this.isDataLoaded = true;
        return;
      }

      this.teamManagementService
        .getTeamCompositionList(employeeIdToUse, filterType)
        .subscribe(
          (res) => {
            const responseData = (res.data || []).sort((a, b) =>
              a.name.localeCompare(b.name),
            );
            this.data = responseData;
            this.displayData = [...this.data];
            this.selectedLocation = 'all';
            this.selectedResourceType = 'all';
            this.extractLocationOptions();
            this.extractResourceTypeOptions();
            this.page = 1;
            this.isDataLoaded = true;
          },
          (error: any) => {
            this.errorHandler.logComponentError(error);
            this.errorMsg =
              TeamCompositionListInfo.FailedToLoadTeamCompositionData;
            this.displayData = [];
            this.isDataLoaded = true;
          },
        );
    } catch (error) {
      this.errorMsg = TeamCompositionListInfo.FailedToLoadTeamCompositionData;
    }
  }

  clearInput(): void {
    this.filterValue = '';
    this.selectedLocation = '';
    this.selectedResourceType = '';
    this.displayData = [...this.data];
    this.page = 1;
    this.isDataLoaded = true;
  }

  searchTimeCompositionList(event: KeyboardEvent): void {
    this.filterValue = this.searchService.getFilteredValue(event);

    this.displayData = this.data?.length
      ? this.searchService.getSearchedData(
          this.data,
          this.filterValue?.toLowerCase(),
        )
      : [];

    this.page = 1;
    this.isDataLoaded = true;
  }

  onLocationSelect(event: any): void {
    this.selectedLocation = this.resolveDropdownValue(event, this.locationOptions);
    this.applyFilters();
  }

  onResourceTypeSelect(event: any): void {
    this.selectedResourceType = this.resolveDropdownValue(event, this.resourceTypeOptions);
    this.applyFilters();
  }

  /** emerald-dropdown emits either the raw {value} object or the option's
   *  label string. Resource Type labels ("Employee (EMP)") don't equal the
   *  underlying values ("emp"), so lowercasing the label breaks the filter.
   *  Resolve back to the value here so both dropdowns behave the same. */
  private resolveDropdownValue(event: any, options: dropdownOptions[]): string {
    if (event == null) return '';
    if (typeof event === 'object' && 'value' in event) return String(event.value ?? '');
    if (typeof event === 'string') {
      const opt = options.find((o) => o.label === event || o.value === event);
      return String(opt?.value ?? event);
    }
    return String(event);
  }

  /** AND-composed filter: intersect location + resource-type selections.
   *  Empty string or "all" at either level means "no filter at that level". */
  private applyFilters(): void {
    const locKey = (this.selectedLocation || 'all').toLowerCase();
    const rtKey = (this.selectedResourceType || 'all').toLowerCase();
    this.displayData = (this.data || []).filter((item) => {
      const matchLoc = locKey === 'all' || locKey === ''
        || (item?.location || '').toLowerCase() === locKey;
      const matchRT = rtKey === 'all' || rtKey === ''
        || (item?.employeeType || '').toLowerCase() === rtKey;
      return matchLoc && matchRT;
    });
    this.page = 1;
    this.isDataLoaded = true;
  }

  extractLocationOptions(): void {
    const uniqueLocations = Array.from(
      new Set(this.data?.map((item) => item?.location?.toLowerCase())),
    ).filter(Boolean);

    this.locationOptions = [
      { label: 'All', value: 'all' },
      ...uniqueLocations.map((loc) => ({
        label: loc.charAt(0).toUpperCase() + loc.slice(1),
        value: loc,
      })),
    ];
  }

  /** Populate the Resource Type dropdown from unique employeeType values on
   *  the fetched roster. Values arrive as EMP / CWR from the BE — keep the
   *  stored value in that raw form (for filter matching) and give it a nicer
   *  display label. */
  extractResourceTypeOptions(): void {
    const uniqueTypes = Array.from(
      new Set(
        (this.data || [])
          .map((item) => (item?.employeeType || '').trim())
          .filter(Boolean)
          .map((t) => t.toLowerCase()),
      ),
    );

    const labelFor = (v: string): string => {
      if (v === 'emp') return 'Employee (EMP)';
      if (v === 'cwr') return 'Contingent (CWR)';
      // Fallback for any unexpected value — title-case it
      return v.charAt(0).toUpperCase() + v.slice(1);
    };

    this.resourceTypeOptions = [
      { label: 'All', value: 'all' },
      ...uniqueTypes.map((t) => ({ label: labelFor(t), value: t })),
    ];
  }

  resetLocationFilter(): void {
    this.selectedLocation = '';
    this.applyFilters();
  }

  navigateToTeamMgmtInfo(empId?: string): void {
    let idToUse = empId;
    if (!idToUse) {
      idToUse = this.data?.[0]?.empId;
    }
    if (idToUse) {
      this.router.navigate([TeamCompositionListInfo.teamManagementinfo], {
        state: { empId: idToUse },
      });
    }
  }

  setSort(field: keyof TeamCompositionList): void {
    if (this.sortField === field) {
      this.sortDir = this.sortDir === 1 ? -1 : 1;
    } else {
      this.sortField = field;
      this.sortDir = 1;
    }
    this.page = 1;
  }

  get sortedData(): TeamCompositionList[] {
    const f = this.sortField;
    return [...(this.displayData || [])].sort((a, b) =>
      String(a?.[f] ?? '').localeCompare(String(b?.[f] ?? '')) * this.sortDir,
    );
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil((this.displayData?.length || 0) / this.pageSize));
  }

  get pageStart(): number {
    return (this.page - 1) * this.pageSize;
  }

  get pageEnd(): number {
    return Math.min(this.pageStart + this.pageSize, this.displayData?.length || 0);
  }

  get pagedData(): TeamCompositionList[] {
    return this.sortedData.slice(this.pageStart, this.pageStart + this.pageSize);
  }

  prevPage(): void { if (this.page > 1) this.page--; }
  nextPage(): void { if (this.page < this.totalPages) this.page++; }

  openTeamDetailsModal(empId?: string) {
    let idToUse = empId;
    if (!idToUse) {
      idToUse = this.data?.[0]?.empId;
    }
    if (idToUse) {
      this.selectedProfileId = idToUse;
      this.showTeamDetailsModal = true;
    }
  }

  closeTeamDetailsModal() {
    this.showTeamDetailsModal = false;
    this.selectedProfileId = null;
  }

  onOpenContractorProfileModal(contractorId: string) {
    this.selectedContractorId = contractorId;
    this.showContractorProfileModal = true;
    if (contractorId) {
      this.selectedProfileId = contractorId;
    }
  }

  closeContractorProfileModal() {
    this.showContractorProfileModal = false;
    this.selectedContractorId = null;
  }

  // ── Row actions ────────────────────────────────────────────────────────

  exclusionLabel(row: TeamCompositionList): string {
    return row.excludeFromIskill
      ? this.TimeCompositionListLabels.INCLUDE_IN_ISKILL
      : this.TimeCompositionListLabels.EXCLUDE_FROM_ISKILL;
  }

  get exclusionDialogTitle(): string {
    return this.actionRow?.excludeFromIskill
      ? this.TimeCompositionListLabels.INCLUDE_TITLE
      : this.TimeCompositionListLabels.EXCLUDE_TITLE;
  }

  get exclusionDialogMessage(): string {
    if (!this.actionRow) return '';
    const who = `${this.actionRow.name} (${this.actionRow.empId})`;
    return this.actionRow.excludeFromIskill
      ? `Include ${who} in iSkill again?\nThey will be back in Team Explorer and can set up their skills and competencies, assessments, learning pathways and readiness.`
      : `Exclude ${who} from iSkill?\nThey will be removed from Team Explorer and will not be able to set up skills and competencies, take assessments, follow learning pathways or count towards readiness. You can include them again later.`;
  }

  get deactivateDialogMessage(): string {
    if (!this.actionRow) return '';
    return `Mark ${this.actionRow.name} (${this.actionRow.empId}) as Inactive?\nThis sets their status to InActive with the last working day below and removes them from this list.`;
  }

  openExclusionDialog(row: TeamCompositionList): void {
    this.actionRow = row;
    this.showExclusionDialog = true;
  }

  openDeactivateDialog(row: TeamCompositionList): void {
    this.actionRow = row;
    this.lastWorkingDay = '';
    this.showDeactivateDialog = true;
  }

  cancelAction(): void {
    if (this.actionInProgress) return;
    this.showExclusionDialog = false;
    this.showDeactivateDialog = false;
    this.actionRow = null;
    this.lastWorkingDay = '';
  }

  confirmExclusion(): void {
    const row = this.actionRow;
    if (!row || this.actionInProgress) return;
    const exclude = !row.excludeFromIskill;
    this.actionInProgress = true;
    this.teamManagementService.setIskillExclusion(row.empId, exclude).subscribe({
      next: () => {
        row.excludeFromIskill = exclude; // in-place: keeps current filters/sort/page
        this.toastr.success(
          `${row.name} ${exclude
            ? this.TimeCompositionListLabels.EXCLUDED_SUCCESS
            : this.TimeCompositionListLabels.INCLUDED_SUCCESS}`,
        );
        this.actionInProgress = false;
        this.cancelAction();
      },
      error: (error) => {
        this.actionInProgress = false;
        this.errorHandler.logComponentError(error);
        this.toastr.error(error?.error?.message || this.TimeCompositionListLabels.ACTION_FAILED);
      },
    });
  }

  confirmDeactivate(): void {
    const row = this.actionRow;
    if (!row || !this.lastWorkingDay || this.actionInProgress) return;
    this.actionInProgress = true;
    this.teamManagementService.deactivateEmployee(row.empId, this.lastWorkingDay).subscribe({
      next: () => {
        // The list only shows Active people — drop the row without a refetch.
        this.data = this.data.filter((r) => r.empId !== row.empId);
        this.displayData = this.displayData.filter((r) => r.empId !== row.empId);
        if (this.page > this.totalPages) this.page = this.totalPages;
        this.toastr.success(`${row.name} ${this.TimeCompositionListLabels.INACTIVE_SUCCESS}`);
        this.actionInProgress = false;
        this.cancelAction();
      },
      error: (error) => {
        this.actionInProgress = false;
        this.errorHandler.logComponentError(error);
        this.toastr.error(error?.error?.message || this.TimeCompositionListLabels.ACTION_FAILED);
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
